import React, { useState, useEffect, ChangeEvent } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { fieldValidator, getResolvedOptions } from '../../utils';
import StyledTooltip from '../StyledTooltip';
import {
  Input,
  ComboBox,
  DatePicker,
  Select,
  Textarea,
  Checkbox,
  TimePicker,
} from '@airbus/components-react';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import {
  valueFormatter,
  applyStringConditions,
  applyNumberConditions,
} from '../../utils/yupSchemaCreator';
import { useColumnContextAPI } from '../../context/GetAllColumnContext';
import style from './RenderEditField.module.css';
import { useGridDataContext } from '../../context/GridDataContext';
import { isSameDate, normalizeDateToUTC } from '../../utils/dateUtils';

export default function GridFields(props) {
  const { language } = useLanguageContextAPI();
  const { allDropdowns, setAllDropdowns, customConfig } = useColumnContextAPI();
  const [, setRows] = useGridDataContext();
  const { id, field, colDef, value: initialValue, row } = props;
  const { type } = colDef;
  const apiRef = useGridApiContext();
  const [error, setError] = useState(null);

  const singleSelectOptions =
    type === 'singleSelect'
      ? getResolvedOptions({
          field,
          allDropdowns,
          customConfig,
        })
      : undefined;

  const handleEnterTabBlur = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      const target = e.target as HTMLInputElement;
      target.blur();
    }
  };

  const blurHandler = (value) => {
    apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
    setRows((prestate) => {
      const oldRow = prestate.find((item) => item.id === row.id);

      if (isSameDate(oldRow[field], value)) {
        return prestate;
      }
      if (oldRow[field] === value) {
        return prestate;
      }
      oldRow[field] = value;
      if (oldRow.status === 'N' || oldRow.status === 'C') {
        oldRow.touched[field] = true;
      }
      oldRow.status = oldRow.status || 'E';
      const newState = [...prestate];
      const index = newState.findIndex((item) => item.id === row.id);
      newState.splice(index, 1, oldRow);
      return newState;
    });
  };

  const fieldProp = {
    initialValue,
    colDef,
    id,
    field,
    setError,
    blurHandler,
    apiRef,
    language,
    row,
    allDropdowns,
    resolvedOptions: singleSelectOptions,
    setAllDropdowns,
    handleEnterTabBlur,
  };

  return (
    <StyledTooltip title={error?.msg}>
      <div className={error?.msg ? `${style.render_edit_cell} Mui-error` : style.render_edit_cell}>
        {(type === 'string' || type === 'number' || type === 'email') && (
          <FieldStringOrNumber {...fieldProp} />
        )}
        {type === 'textarea' && <FieldTextarea {...fieldProp} />}
        {type === 'singleSelect' && <FieldSingleSelect {...fieldProp} />}
        {type === 'multiSelect' && <FieldMultiSelect {...fieldProp} />}
        {type === 'checkBox' && <FieldCheckBox {...fieldProp} />}
        {type === 'boolean' && <FieldBooleanSelect {...fieldProp} />}
        {type === 'date' && <FieldDate {...fieldProp} />}
        {type === 'time' && <FieldTime {...fieldProp} />}
      </div>
    </StyledTooltip>
  );
}

const FieldStringOrNumber = (prop) => {
  const {
    initialValue,
    colDef,
    id,
    field,
    setError,
    blurHandler,
    apiRef,
    language,
    row,
    handleEnterTabBlur,
  } = prop;
  const [value, setValue] = useState(initialValue);

  const onStringOrNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (colDef.type === 'number' && isNaN(+event.target.value)) {
      return;
    }
    const newValue = event.target.value;
    const error = fieldValidator(colDef, newValue, language);
    setError(error);
    const conditionsProp = { colDef, error, newValue };
    const isValid =
      colDef.type === 'number'
        ? applyNumberConditions(conditionsProp)
        : applyStringConditions(conditionsProp);
    if (isValid) {
      const result = colDef.type === 'number' ? valueFormatter(newValue) : newValue;
      let resp: String = result;
      setValue(newValue);
      if (colDef?.onValueChange) {
        resp = colDef.onValueChange({ rowId: id, value: result, row }) ?? result;
      }
      apiRef.current.setEditCellValue({ id, field, value: resp });
    }
  };

  return (
    <Input
      type={colDef?.attrDataType === 'email' ? 'email' : 'text'}
      value={value ?? ''}
      onChange={(event) => {
        onStringOrNumberChangeHandler(event);
      }}
      onBlur={() => blurHandler(colDef.type === 'number' && value === '' ? null : value)}
      onKeyDown={handleEnterTabBlur}
      autoFocus={true}
    />
  );
};

const FieldTextarea = (prop) => {
  const {
    initialValue,
    colDef,
    id,
    field,
    setError,
    blurHandler,
    apiRef,
    language,
    row,
    handleEnterTabBlur,
  } = prop;
  const [value, setValue] = useState(initialValue);

  const onTextareaChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    const error = fieldValidator(colDef, newValue, language);
    setError(error);
    const conditionsProp = { colDef, error, newValue };
    const isValid = colDef.type === 'textarea' ? applyStringConditions(conditionsProp) : '';
    if (isValid) {
      if (colDef?.onValueChange) {
        colDef.onValueChange({ rowId: id, value: newValue, row });
      }
      apiRef.current.setEditCellValue({ id, field, value: newValue });
    }
  };
  return (
    <Textarea
      rows={2}
      value={value ?? ''}
      onChange={(event) => onTextareaChangeHandler(event)}
      onBlur={() => blurHandler(value)}
      onKeyDown={handleEnterTabBlur}
      autoFocus={true}
    />
  );
};

const FieldSingleSelect = (prop) => {
  const {
    initialValue,
    colDef,
    id,
    field,
    apiRef,
    row,
    allDropdowns,
    blurHandler,
    setAllDropdowns,
    resolvedOptions,
    handleEnterTabBlur,
  } = prop;
  const options =
    resolvedOptions ?? allDropdowns[field]?.filter((item) => item.value != null) ?? [];
  const [valueOptions, setValueOptions] = useState(options);
  const [selectedObj, setSelectedObj] = useState(null);

  useEffect(() => {
    focusField({ id, field });
  }, []);

  useEffect(() => {
    const valueObj = valueOptions?.find((item) => item.value == initialValue);
    if (valueObj) {
      setSelectedObj(valueObj);
    }
  }, [initialValue]);

  const onValueChangeHandler = (obj) => {
    const { value } = obj ?? {};
    const currentValue = selectedObj?.value ?? null;
    if (currentValue === value) {
      return;
    }

    const valueObj = valueOptions.find((item) => item.value == value);
    if (valueObj?.disabled) {
      return;
    }
    setSelectedObj(valueObj);
    if (colDef?.onValueChange) {
      const resp = colDef.onValueChange({ rowId: id, value, row });
      if (resp === false) {
        apiRef.current.setEditCellValue({ id, field, value: initialValue });
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
        return;
      }
    }
    apiRef.current.setEditCellValue({ id, field, value: value ?? null });
  };
  const handleCreate = (value) => {
    const newOption = { label: value, value };
    setValueOptions((options) => [...options, newOption]);
    setSelectedObj(newOption);
    setAllDropdowns((prestate) => {
      const newstate = { ...prestate };
      newstate[field] = [...valueOptions, newOption];
      return newstate;
    });
    setValueOptions((options) => [...options, newOption]);
    if (colDef?.onValueChange) {
      const resp = colDef.onValueChange({ rowId: id, value, row });
      if (resp === false) {
        apiRef.current.setEditCellValue({ id, field, value: initialValue });
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
        return;
      }
    }
    apiRef.current.setEditCellValue({ id, field, value: value });
  };

  return colDef.creatable ? (
    <ComboBox
      creatable
      className='h-100'
      id={id + '_' + field}
      hideClear={true}
      options={valueOptions}
      value={selectedObj}
      onChange={onValueChangeHandler}
      onCreate={handleCreate}
      onBlur={() => blurHandler(selectedObj?.value ?? null)}
      onKeyDown={handleEnterTabBlur}
    />
  ) : (
    <ComboBox
      className='h-100'
      id={id + '_' + field}
      hideClear={true}
      options={valueOptions}
      value={selectedObj}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value}
      onChange={onValueChangeHandler}
      onBlur={() => blurHandler(selectedObj?.value ?? null)}
      onKeyDown={handleEnterTabBlur}
    />
  );
};
const FieldMultiSelect = (prop) => {
  const {
    initialValue,
    colDef,
    id,
    field,
    apiRef,
    row,
    allDropdowns,
    blurHandler,
    handleEnterTabBlur,
  } = prop;
  const [selectedObj, setSelectedObj] = useState(null);
  const valueOptions = allDropdowns[field];
  useEffect(() => {
    const valueArr = valueOptions?.filter((item) => initialValue.includes(item.value));
    if (valueArr) {
      setSelectedObj(valueArr);
    }
  }, [initialValue]);

  useEffect(() => {
    focusField({ id, field });
  }, []);

  const onValueChangeHandler = (valueArr) => {
    setSelectedObj(valueArr);
    if (colDef?.onValueChange) {
      const resp = colDef.onValueChange({
        rowId: id,
        value: valueArr.map((item) => item.value),
        row,
      });
      if (resp === false) {
        apiRef.current.setEditCellValue({ id, field, value: initialValue });
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: false });
        return;
      }
    }
    apiRef.current.setEditCellValue({
      id,
      field,
      value: valueArr.map((item) => item.value) ?? null,
    });
  };

  return (
    <ComboBox
      multiple
      id={id + '_' + field}
      className='h-100'
      hideClear={true}
      options={valueOptions}
      value={selectedObj}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value}
      onChange={onValueChangeHandler}
      onBlur={() => blurHandler(selectedObj?.map((item) => item.value))}
      onKeyDown={handleEnterTabBlur}
    />
  );
};
const FieldBooleanSelect = (prop) => {
  const { initialValue, id, field, apiRef, colDef, row } = prop;
  const [selectedValue, setSelectedValue] = useState(null);
  const booleanOptions = [
    { label: 'true', value: 'true' },
    { label: 'false', value: 'false' },
  ];
  useEffect(() => {
    if (initialValue) {
      setSelectedValue(initialValue.toString());
    }
  }, [initialValue]);

  const onBooleanChangeHandler = (event, val) => {
    BooleanOnChangeHandler({ val, colDef, apiRef, id, field, row });
  };

  return (
    <Select
      className='h-100'
      options={booleanOptions}
      value={selectedValue}
      onChange={onBooleanChangeHandler}
    ></Select>
  );
};

const FieldCheckBox = (prop) => {
  const { id, field, apiRef, colDef, row } = prop;

  const onChangeHandler = (val) => {
    BooleanOnChangeHandler({ val, colDef, apiRef, id, field, row });
  };

  return <Checkbox onChange={onChangeHandler} />;
};

const BooleanOnChangeHandler = (props) => {
  const { val, colDef, apiRef, id, field, row } = props;
  let newValue = false;
  if (val === 'true') {
    newValue = true;
  }
  if (colDef?.onValueChange) {
    colDef.onValueChange({ rowId: id, value: newValue, row });
  }
  apiRef.current.setEditCellValue({ id, field, value: newValue });
};

const FieldDate = (prop) => {
  const { initialValue, colDef, id, field, setError, blurHandler, apiRef, language, row } = prop;
  const minDates = colDef?.attrMinValue ? new Date(colDef?.attrMinValue, 1, 1) : null;
  const maxDates = colDef?.attrMaxValue ? new Date(colDef?.attrMaxValue, 12, 31) : null;
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const onDateChangeHandler = (val) => {
    const newValue = normalizeDateToUTC(val) ?? null;
    setValue(newValue);
    const error = fieldValidator(colDef, newValue, language);
    setError(error);
    if (colDef?.onValueChange) {
      colDef.onValueChange({ rowId: id, value: newValue, row });
    }
    apiRef.current.setEditCellValue({ id, field, value: newValue });
    blurHandler(newValue);
  };

  return (
    <div style={{ height: '100%' }} tabIndex={0}>
      <DatePicker
        value={value}
        dateFormat='DD/MM/YYYY'
        datePattern='[0-9]{2}/[0-9]{2}/[0-9]{4}'
        onChange={(val) => onDateChangeHandler(val)}
        minDate={minDates}
        maxDate={maxDates}
        inputProps={{ autoFocus: true }}
        dateSeparator='/'
      />
    </div>
  );
};

const FieldTime = (prop) => {
  const { initialValue, colDef, id, field, setError, blurHandler, apiRef, language, row } = prop;
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const onTimeChangeHandler = (val) => {
    const newValue = val ?? null;
    setValue(newValue);
    const error = fieldValidator(colDef, newValue, language);
    setError(error);
    if (colDef?.onValueChange) {
      colDef.onValueChange({ rowId: id, value: newValue, row });
    }
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <div style={{ height: '100%' }} tabIndex={0} onBlur={() => blurHandler(value)}>
      <TimePicker
        value={value}
        onChange={(val) => onTimeChangeHandler(val)}
        inputProps={{ autoFocus: true }}
      />
    </div>
  );
};

export const focusField = ({ id, field }) => {
  setTimeout(() => {
    const element = document.getElementById(`${id}_${field}`);
    const toggle = document.getElementById(`${id}_${field}-toggle-button`);
    if (element) {
      element.focus();
      toggle.click();
    }
  }, 10);
};
